import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {EventsService} from "../../../events/events.service";
import {EventCategoryListItem} from "../../../events/types/event-category-list-item";
import {SlickCarouselComponent, SlickCarouselModule} from "ngx-slick-carousel";
import {CategoryFilterItem} from "./types/category-filter-item";
import {Event, EventArtist} from "../../../events/types/event";
import {RouterLink} from "@angular/router";
import {EventFilters} from "./types/event-filters";
import {EventsFilterResponse} from "../../../events/types/events-filter-response";
import {EventsCalendar} from "./types/events-calendar";
import {environment} from "../../../../environments/environment";

@Component({
  selector: 'app-widget-events-calendar',
  standalone: true,
  imports: [CommonModule, SlickCarouselModule, RouterLink],
  templateUrl: './events-calendar.component.html',
  styleUrls: [
    './events-calendar.component.less',
    '../../../../styles/events/_events-list.less'
  ]
})
export class EventsCalendarComponent implements OnInit, AfterViewInit {

  @Input() widget !: EventsCalendar;
  @ViewChild('yearsSlider') yearsSlider!: SlickCarouselComponent;
  @ViewChild('datesSlider') datesSlider!: SlickCarouselComponent;

  public slideConfig = {
    infinite: false,
    slidesToShow: 10,
    slidesToScroll: 1,
    responsive: [{
      breakpoint: 1024, settings: {
        slidesToShow: 6,
      }
    }, {
      breakpoint: 600, settings: {
        slidesToShow: 4,
      }
    }, {
      breakpoint: 480, settings: {
        slidesToShow: 3,
      }
    }]
  };

  public yearsSlideConfig = {
    infinite: false,
    slidesToShow: 'auto',
    arrows: false,
    focusOnSelect: true,
    responsive: [
      {
        breakpoint: 480,
        settings: {
          centerMode: true,
          variableWidth: true,
          centerPadding: '20px',
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }]
  };

  public categories!: CategoryFilterItem[];
  public events!: Event[];
  public eventDates!: string[];
  public viewMode!: 'list' | 'grid';
  public selectedDay: string | null = null;
  public selectedYear: number | null = null;
  private filtersUnchanged: boolean = true;

  private eventFilters: EventFilters = {
    categoryIds: [],
    dateFrom: `${new Date().getFullYear()}-01-01 00:00:00`,
    dateTo: `${new Date().getFullYear()}-12-31 23:59:59`
  };

  protected trackByFn(index: number, item: string) {
    return index;
  };

  public constructor(
    private eventsService: EventsService,
  ) {
  }

  ngOnInit(): void {
    this.viewMode = this.widget.data.view ?? 'list';
    this.selectedYear = (new Date()).getFullYear();

    this.eventsService.getCategoriesList()
      .subscribe(
        (categories: EventCategoryListItem[]) => {
          this.categories = categories.map((c) => {
            return {
              category: c,
              selected: false,
            }
          })
        }
      );

    this.loadEvents();
  }

  ngAfterViewInit() {
    this.yearsSlider.slickGoTo(
      this.widget.data.years.findIndex(y => y === this.selectedYear),
    );
  }

  private loadEvents(): void {
    this.eventsService.getEvents(
      this.eventFilters.categoryIds,
      this.eventFilters.dateFrom,
      this.eventFilters.dateTo,
    )
      .subscribe(
        (response: EventsFilterResponse) => {
          this.events = response.items;
          this.eventDates = response.days;

          // Scroll to next upcoming date after dates are loaded
          setTimeout(() => this.scrollToNextUpcomingDate(), 100);
        }
      );
  }

  private scrollToNextUpcomingDate(): void {
    if (!this.datesSlider || !this.eventDates || this.eventDates.length === 0) {
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find the index of the first date that is today or in the future
    const nextDateIndex = this.eventDates.findIndex(dateString => {
      const eventDate = new Date(dateString);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today;
    });

    // If we found a future date, scroll to it
    if (nextDateIndex !== -1) {
      // Calculate how many slides are visible based on current viewport
      const slidesToShow = this.getSlidesToShow();

      // Adjust the target index to account for the fact that slick can't scroll
      // past the point where the last slide is visible
      const maxScrollIndex = Math.max(0, this.eventDates.length - slidesToShow);
      const targetIndex = Math.min(nextDateIndex, maxScrollIndex);

      this.datesSlider.slickGoTo(targetIndex);
    }
  }

  private getSlidesToShow(): number {
    const width = window.innerWidth;

    // Match the breakpoints from slideConfig
    if (width < 480) {
      return 3;  // slidesToShow: 3
    } else if (width < 600) {
      return 4;  // slidesToShow: 4
    } else if (width < 1024) {
      return 6;  // slidesToShow: 6
    } else {
      return 10; // slidesToShow: 10
    }
  }

  public getCategoryButtonClasses(category: EventCategoryListItem): string[] {
    return [
      `button-event-category-style--${category.style}`
    ]
  }

  public getEventArtists(event: Event): string {
    return event.artists.filter(m => m.name).map(a => this.getArtistText(a)).join(', ');
  }

  private getArtistText(artist: EventArtist) {
    if (!artist.isGroup) {
      return artist.name.trim();
    }

    return `<strong>${artist.name}</strong> | ${artist.members.filter(m => m.name).map(m => m.name.trim()).join(', ')}`;
  }

  public getEventStyle(event: Event): string[] {
    return [
      `event-style--${event.style}`
    ];
  }

  public getEventImageUrl(event: Event): string | null {
    if (!event.image) {
      return null;
    }

    const basePath = `${environment.cdn.url}/events/thumbnails/medium`;
    const webpImage = event.image.replace(/\.(jpe?g|png)$/i, '.webp');

    // Use image-set for WebP with fallback
    return `image-set(url(${basePath}/${webpImage}) type("image/webp"), url(${basePath}/${event.image}))`;
  }

  public getEventCopyrights(event: Event): string {
    return event.copyrights || event.name;
  }

  public getEventDisplayName(event: Event): string {
    return event.name;
  }

  public filterBySelectedDate(event: Event): boolean {
    if (this.noFilters()) {
      const eventDate = new Date(event.startTime);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (eventDate < today) {
        return false;
      }
    }
    return this.selectedDay === null || event.startTime.startsWith(this.selectedDay);
  }

  public onClickCategoryFilter(category: CategoryFilterItem): void {
    this.filtersUnchanged = false;
    category.selected = !category.selected;
    this.eventFilters.categoryIds = this.categories.filter(c => c.selected).map(c => c.category.id);
    this.loadEvents();
  }

  public onClickYearFilter(year: number) {
    this.filtersUnchanged = false;
    this.eventFilters.dateFrom = `${year}-01-01`;
    this.eventFilters.dateTo = `${year}-12-31 23:59:59`;
    this.selectedDay = null;
    this.selectedYear = year;
    this.loadEvents()
  }

  public onClickEventDay(day: string): void {
    if (this.selectedDay === day) {
      this.selectedDay = null;
      return;
    }

    this.selectedDay = day;
  }

  public setViewMode(value: 'list' | 'grid'): void {
    this.viewMode = value;
  }

  private noFilters(): boolean {
    return this.filtersUnchanged && !this.selectedDay;
  }
}
